import { useState, useEffect, useCallback } from 'react';
import {
  loadFavorites, addToFavorites, removeFromFavorites, isFavorite,
  loadTriedFlavors, addToTriedFlavors, removeFromTriedFlavors, hasTried,
  getNote, updateNote, getUserRating, rateFlavor,
  loadTheme, saveTheme
} from '../utils/storageUtils';

/**
 * Custom hook for managing user preferences
 * @returns - Object containing user preferences and functions to update them
 */
export const useUserPreferences = () => {
  // State for favorites
  const [favorites, setFavorites] = useState<string[]>([]);
  
  // State for tried flavors
  const [triedFlavors, setTriedFlavors] = useState<{ id: string, date: string }[]>([]);
  
  // State for theme
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  
  // Load user preferences on mount
  useEffect(() => {
    setFavorites(loadFavorites());
    setTriedFlavors(loadTriedFlavors());
    setTheme(loadTheme() as 'light' | 'dark');
  }, []);
  
  // Add to favorites
  const addFavorite = useCallback((flavorId: string) => {
    const updatedFavorites = addToFavorites(flavorId);
    setFavorites(updatedFavorites);
    return updatedFavorites;
  }, []);
  
  // Remove from favorites
  const removeFavorite = useCallback((flavorId: string) => {
    const updatedFavorites = removeFromFavorites(flavorId);
    setFavorites(updatedFavorites);
    return updatedFavorites;
  }, []);
  
  // Check if flavor is in favorites
  const checkIsFavorite = useCallback((flavorId: string) => {
    return isFavorite(flavorId);
  }, []);
  
  // Add to tried flavors
  const addTriedFlavor = useCallback((flavorId: string) => {
    const updatedTriedFlavors = addToTriedFlavors(flavorId);
    setTriedFlavors(updatedTriedFlavors);
    return updatedTriedFlavors;
  }, []);
  
  // Remove from tried flavors
  const removeTriedFlavor = useCallback((flavorId: string) => {
    const updatedTriedFlavors = removeFromTriedFlavors(flavorId);
    setTriedFlavors(updatedTriedFlavors);
    return updatedTriedFlavors;
  }, []);
  
  // Check if flavor has been tried
  const checkHasTried = useCallback((flavorId: string) => {
    return hasTried(flavorId);
  }, []);
  
  // Get note for flavor
  const getFlavorNote = useCallback((flavorId: string) => {
    return getNote(flavorId);
  }, []);
  
  // Update note for flavor
  const updateFlavorNote = useCallback((flavorId: string, note: string) => {
    return updateNote(flavorId, note);
  }, []);
  
  // Get rating for flavor
  const getFlavorRating = useCallback((flavorId: string) => {
    return getUserRating(flavorId);
  }, []);
  
  // Rate flavor
  const rateFlavorFn = useCallback((flavorId: string, rating: number) => {
    return rateFlavor(flavorId, rating);
  }, []);
  
  // Toggle theme
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    saveTheme(newTheme);
    setTheme(newTheme);
    return newTheme;
  }, [theme]);
  
  return {
    favorites,
    triedFlavors,
    theme,
    addFavorite,
    removeFavorite,
    checkIsFavorite,
    addTriedFlavor,
    removeTriedFlavor,
    checkHasTried,
    getFlavorNote,
    updateFlavorNote,
    getFlavorRating,
    rateFlavor: rateFlavorFn,
    toggleTheme
  };
};
